/**
 * PendingReferralGate — brings a signed-in person with a referral link pending on their device back to that link's
 * invitation page (the referral program, docs/PLAN-referral-program-20261019.md). The referral sibling of
 * PendingInviteGate.
 *
 * A person may open a referral link, then sign up from the home page, another tab, or days later. However they got
 * here, their first app surface sends them to /referrals/join/CODE, where the claim runs (the guardrails decide) and
 * the record is cleared on every outcome — claimed, refused, or "continue without it" — so at most one bounce ever
 * happens. A team invitation in progress comes first: its gate owns the person until it resolves.
 */
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { isGatedAppPath } from '../../hooks/trialOnboardingGate';
import { getPendingInvite } from '../../lib/pendingInvite';
import { getPendingReferral, isReferralLandingPath, referralLandingPath } from '../../lib/referralPending';

/** Where the gate sends a signed-in person, or null to stay. Pure apart from the two device markers it reads. */
export function pendingReferralTarget(pathname: string): string | null {
  const path = (pathname || '').toLowerCase();
  if (isReferralLandingPath(path) || path.startsWith('/auth') || path.startsWith('/accept-invite')) return null;
  const onboarding = path === '/onboarding' || path.startsWith('/onboarding/');
  if (!isGatedAppPath(pathname) && path !== '/' && !onboarding) return null;
  if (getPendingInvite()) return null;
  const pending = getPendingReferral();
  return pending ? referralLandingPath(pending.code) : null;
}

export const PendingReferralGate: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  if (!isAuthenticated) return null;
  const target = pendingReferralTarget(location.pathname);
  return target ? <Navigate to={target} replace /> : null;
};

export default PendingReferralGate;
